/**
 * Recipe Adapter
 *
 * Converts the self-contained recipe.json format into the existing
 * `TemplateDefinition` shape so the UI pipeline (gallery, preview,
 * template mode) works unchanged.
 */

import type { Recipe } from '@/types/recipe';
import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';
import {
  TEMPLATE_CATEGORIES,
  type TemplateCategory,
  type TemplateDefinition,
} from '@/types/template';

// =============================================================================
// PUBLIC TYPES
// =============================================================================

/** Extra data from a recipe that doesn't map onto TemplateDefinition. */
export interface RecipeExtras {
  /** Trigger sample payload — seeded into mockedNodes[`trigger-<name>`]. */
  triggerSampleData?: TriggerMockPayload;
  /** Per-action mock state keyed by recipe action name. */
  mocks?: Record<string, MockNodeInfo>;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Map free-form category strings to `TemplateCategory[]` enum values.
 * Unrecognised entries are dropped; an empty result falls back to
 * `['automation']` so every template lands in at least one gallery bucket.
 */
function mapCategory(raw: string[]): TemplateCategory[] {
  const valid = new Set<TemplateCategory>();
  for (const entry of raw) {
    const normalised = entry.toLowerCase().trim();
    const match = (TEMPLATE_CATEGORIES as readonly string[]).find((c) => c === normalised);
    if (match) valid.add(match as TemplateCategory);
  }
  return valid.size > 0 ? [...valid] : ['automation'];
}

/**
 * Map the recipe `source` field to the `TemplateSource` type.
 * "builtin" → "built-in"; anything else passes through if valid.
 */
function mapSource(raw: string | undefined): 'built-in' | 'community' {
  if (!raw || raw === 'builtin' || raw === 'built-in') return 'built-in';
  if (raw === 'community') return 'community';
  return 'built-in';
}

// =============================================================================
// ADAPTER — Recipe → TemplateDefinition
// =============================================================================

/**
 * Convert a validated `Recipe` to the existing `TemplateDefinition` shape
 * so it can be consumed by the gallery and template-mode pipeline.
 */
export function recipeToTemplateDefinition(recipe: Recipe): TemplateDefinition {
  const { metadata, definition, connections } = recipe;

  // Build connectionRequirements array from the connections record
  const connectionRequirements = Object.entries(connections).map(([key, conn]) => ({
    connectorType: conn.connectorType,
    apiId: conn.apiId,
    referenceKey: key,
  }));

  // Recipe `notes` is a top-level field (peer to mocks/trigger/connections);
  // fold it onto the embedded WorkflowDefinition so setWorkflowFiles lands it
  // into workflow.notes and the canvas renders the sticky notes.
  const recipeNotes = recipe.notes;
  const workflowDefinition =
    recipeNotes && Object.keys(recipeNotes).length > 0
      ? { ...definition, notes: { ...(definition.notes ?? {}), ...recipeNotes } }
      : definition;

  return {
    id: metadata.id,
    name: metadata.name,
    description: metadata.description,
    category: mapCategory(metadata.category),
    version: '1.0.0',
    tags: metadata.tags.map((t) => t.toLowerCase()),
    connectionRequirements,
    source: mapSource(metadata.source),
    workflowDefinition,
    ...(recipe.featuredConnectors ? { featuredConnectors: recipe.featuredConnectors } : {}),
  };
}

// =============================================================================
// EXTRAS — Recipe-only data that doesn't fit in TemplateDefinition
// =============================================================================

/**
 * Extract recipe-specific extras (trigger sample data, mocks) that are not
 * represented in `TemplateDefinition` but are needed by template mode.
 *
 * Trigger output is reshaped into a {@link TriggerMockPayload} so it can be
 * dropped straight into `mockedNodes['trigger-<name>']`. Each entry in
 * `recipe.mocks` becomes a {@link MockNodeInfo} — either a `runForReal: true`
 * opt-out or `{ mockOutput: { status, outputs: { … } } }`.
 */
export function extractRecipeExtras(recipe: Recipe): RecipeExtras {
  const extras: RecipeExtras = {};

  // Trigger sample data — TriggerMockPayload shape (matches the API contract).
  if (recipe.trigger?.outputs) {
    const t = recipe.trigger.outputs;
    extras.triggerSampleData = {
      method: t.method,
      headers: t.headers as Record<string, string>,
      queries: t.queries as Record<string, string>,
      ...(t.body !== undefined ? { body: t.body } : {}),
    };
  }

  // Per-action mocks — emit pre-shaped MockNodeInfo entries.
  if (Object.keys(recipe.mocks).length > 0) {
    const mocks: Record<string, MockNodeInfo> = {};
    for (const [actionName, mock] of Object.entries(recipe.mocks)) {
      if (mock.runForReal === true) {
        mocks[actionName] = { actionName, runForReal: true };
        continue;
      }
      // Narrowed: the non-opt-out variant has `status` and `outputs`.
      if (!('status' in mock)) continue;
      mocks[actionName] = {
        actionName,
        mockOutput: {
          status: mock.status,
          outputs: {
            statusCode: mock.outputs.statusCode,
            ...(mock.outputs.body !== undefined ? { body: mock.outputs.body } : {}),
            ...(mock.outputs.headers !== undefined
              ? { headers: mock.outputs.headers as Record<string, string> }
              : {}),
          },
        },
      };
    }
    extras.mocks = mocks;
  }

  return extras;
}
